import { useEffect, useState } from "react";
import {
  IconArrowBackUp,
  IconArrowsJoin,
  IconArrowsLeftRight,
  IconArrowLeft,
  IconArrowRampRight,
  IconArrowRight,
  IconArrowUp,
  IconChevronDown,
  IconGitBranch,
  IconHome2,
  IconLanguage,
  IconPhoto,
  IconParking,
  IconBuildingSkyscraper,
  IconMapPin,
  IconPlayerPlay,
  IconRoad,
  IconRotateClockwise2,
  IconSparkles,
  IconTrash,
  IconToolsKitchen2,
  IconMusic,
} from "@tabler/icons-react";
import InlineError from "@components/forms/InlineError";
import AppModal from "@components/modals/AppModal";
import MediaPicker from "@components/media/MediaPicker";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import { resolveMediaKind, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  generateTextToSpeechRequest,
  listAiVoicesCatalogRequest,
  searchAiVoicesRequest,
} from "@services/ai/aiApi";
import { downloadAudioWithCustomNameRequest } from "@services/multimedia/multimediaApi";
import { TURN_TYPE_DEFINITIONS } from "@utils/mode_tool/turnType";
import {
  hasRoutePointMedia,
  hasRoutePointName,
  hasRoutePointRadius,
  hasRoutePointTurnCase,
  hasRoutePointType,
  isNewRoutePoint,
} from "@utils/mode_tool/validation";
import {
  buildDrivingRouteGroupName,
  DRIVING_ALTERNATE_ROUTE_KEYS,
  normalizeDrivingRouteGroupKey,
} from "@utils/mode_tool/modeRoute";
import {
  buildAudioFileName,
  EMPTY_AI_SPEAKER_SETTINGS,
  findAiVoice,
  hasEditorContent,
  htmlToPlainText,
  readLocalizedValue,
} from "@utils/mode_tool/pointEditorHelpers";
import { buildTtsPayload } from "@utils/mode_tool/ttsHelpers";
import ModalQuillEditor from "@components/mode_tool/ModalQuillEditor";
import AiAudioWavePreview from "@components/mode_tool/AiAudioWavePreview";
import VoiceAnnotatedTextEditor from "@components/mode_tool/VoiceAnnotatedTextEditor";
import { AiSpeakerConfiguration } from "@components/mode_tool/AiSpeakerConfiguration";

const TURN_TYPE_ICON_BY_KEY = {
  point: IconMapPin,
  continueStraight: IconArrowUp,
  rightTurn: IconArrowRight,
  leftTurn: IconArrowLeft,
  uTurn: IconArrowBackUp,
  roundaboutTurn: IconRotateClockwise2,
  mergeTurn: IconArrowsJoin,
  slipRoadTurn: IconGitBranch,
  laneChangeFromRight: IconRoad,
  laneChangeFromLeft: IconRoad,
  offsetTurn: IconArrowRampRight,
  dividedHighwayCrossover: IconArrowsLeftRight,
  parking: IconParking,
};

const POINT_TYPE_OPTIONS = [
  { value: "building", label: "Building", Icon: IconBuildingSkyscraper },
  { value: "parking", label: "Parking", Icon: IconParking },
  { value: "restaurant", label: "Restaurant", Icon: IconToolsKitchen2 },
  { value: "hotel", label: "Hotel", Icon: IconHome2 },
];

function getRouteGroupSelectValue(point = {}) {
  return normalizeDrivingRouteGroupKey(
    point?.routeGroup ?? point?.route_group ?? "",
    point?.routeGroupName ?? point?.route_group_name ?? "",
    Number(point?.order || 0) || 0,
  );
}

function ModeToolPointEditor({
  point,
  allModePoints,
  selectedMode,
  languages,
  activeLanguageIso,
  onCommitPoint,
  onTranslatePoint,
  onGeneratePointImage,
  onGeneratePointMusic,
  isGeneratingMusic = false,
  onMixAudioMusic,
  isMixingAudio = false,
  isAudioMixBusy = false,
  audioMixError = "",
  onUploadPointMedia,
  onDeletePoint,
}) {
  const languageList = useStartPointLanguages(languages);
  const [draft, setDraft] = useState(point || null);
  const [editor, setEditor] = useState({
    isOpen: false,
    languageIso: "",
    field: "description",
  });
  const [editorHtml, setEditorHtml] = useState("");
  const [touched, setTouched] = useState({
    name: false,
    radiusMeters: false,
    turnCase: false,
    pointType: false,
  });
  const [isTurnTypePickerOpen, setIsTurnTypePickerOpen] = useState(false);
  const [isRouteGroupPickerOpen, setIsRouteGroupPickerOpen] = useState(false);
  const [isPointTypePickerOpen, setIsPointTypePickerOpen] = useState(false);
  const [descriptionModalTab, setDescriptionModalTab] = useState("description");
  const [isDescriptionSaved, setIsDescriptionSaved] = useState(false);
  const [aiSpeakerOneSettings, setAiSpeakerOneSettings] = useState({
    ...EMPTY_AI_SPEAKER_SETTINGS,
  });
  const [aiSpeakerTwoSettings, setAiSpeakerTwoSettings] = useState({
    ...EMPTY_AI_SPEAKER_SETTINGS,
  });
  const [aiNarrationOptions, setAiNarrationOptions] = useState({
    language: [],
    gender: [],
    pitch: [],
    style: [],
    useCase: [],
    vocalTags: [],
  });
  const [aiGeneratedDescription, setAiGeneratedDescription] = useState("");
  const [aiVoiceAssignments, setAiVoiceAssignments] = useState([]);
  const [aiSpeakerOneVoices, setAiSpeakerOneVoices] = useState([]);
  const [aiSpeakerTwoVoices, setAiSpeakerTwoVoices] = useState([]);
  const [aiSpeakerOneVoiceGroups, setAiSpeakerOneVoiceGroups] = useState([]);
  const [aiSpeakerTwoVoiceGroups, setAiSpeakerTwoVoiceGroups] = useState([]);
  const [aiSpeakerOnePageToken, setAiSpeakerOnePageToken] = useState("");
  const [aiSpeakerTwoPageToken, setAiSpeakerTwoPageToken] = useState("");
  const [aiSpeakerOnePreviousTokens, setAiSpeakerOnePreviousTokens] = useState(
    [],
  );
  const [aiSpeakerTwoPreviousTokens, setAiSpeakerTwoPreviousTokens] = useState(
    [],
  );
  const [aiSpeakerOneNextPageToken, setAiSpeakerOneNextPageToken] =
    useState("");
  const [aiSpeakerTwoNextPageToken, setAiSpeakerTwoNextPageToken] =
    useState("");
  const [aiSpeakerOneSearch, setAiSpeakerOneSearch] = useState("");
  const [aiSpeakerTwoSearch, setAiSpeakerTwoSearch] = useState("");
  const [aiVoice, setAiVoice] = useState("");
  const [aiSecondVoice, setAiSecondVoice] = useState("");
  const [aiSpeakerOneSelectedVoice, setAiSpeakerOneSelectedVoice] =
    useState(null);
  const [aiSpeakerTwoSelectedVoice, setAiSpeakerTwoSelectedVoice] =
    useState(null);
  const [hasSecondSpeaker, setHasSecondSpeaker] = useState(false);
  const [aiAudio, setAiAudio] = useState(null);
  const [isLoadingAiSpeakerOneVoices, setIsLoadingAiSpeakerOneVoices] =
    useState(false);
  const [isLoadingAiSpeakerTwoVoices, setIsLoadingAiSpeakerTwoVoices] =
    useState(false);
  const [isLoadingAiCatalog, setIsLoadingAiCatalog] = useState(false);
  const [hasLoadedAiCatalog, setHasLoadedAiCatalog] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isDownloadingAiAudio, setIsDownloadingAiAudio] = useState(false);
  const [aiError, setAiError] = useState("");

  useEffect(() => {
    // Local draft mirrors selected point so editing stays responsive while typing.
    setDraft(point || null);
  }, [point]);

  useEffect(() => {
    if (selectedMode !== "random") return;
    if (!draft?.id) return;
    if (String(draft?.pointType || "").trim()) return;
    commitDraftImmediately({
      ...(draft || {}),
      pointType: POINT_TYPE_OPTIONS[0]?.value || "building",
    });
  }, [draft, selectedMode]);

  useEffect(() => {
    const isAiTabOpen =
      editor.isOpen &&
      editor.field === "description" &&
      descriptionModalTab === "ai-audio-description";
    if (
      !isAiTabOpen ||
      (hasLoadedAiCatalog && aiNarrationOptions.vocalTags.length)
    ) {
      return;
    }

    let isCurrent = true;
    setIsLoadingAiCatalog(true);
    setAiError("");
    listAiVoicesCatalogRequest()
      .then((response) => {
        if (!isCurrent) return;
        const catalog = response?.body || {};
        setAiNarrationOptions({
          language: Array.isArray(catalog.language) ? catalog.language : [],
          gender: Array.isArray(catalog.gender) ? catalog.gender : [],
          pitch: Array.isArray(catalog.pitch) ? catalog.pitch : [],
          style: Array.isArray(catalog.style) ? catalog.style : [],
          useCase: Array.isArray(catalog.useCase) ? catalog.useCase : [],
          vocalTags: Array.isArray(catalog.vocalTags) ? catalog.vocalTags : [],
        });
        setHasLoadedAiCatalog(true);
      })
      .catch((error) => {
        if (isCurrent) {
          setAiError(error?.message || "Could not load the AI voice catalog.");
        }
      })
      .finally(() => {
        if (isCurrent) setIsLoadingAiCatalog(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [
    descriptionModalTab,
    editor.field,
    editor.isOpen,
    hasLoadedAiCatalog,
    aiNarrationOptions.vocalTags.length,
  ]);

  useEffect(() => {
    const isAiTabOpen =
      editor.isOpen &&
      editor.field === "description" &&
      descriptionModalTab === "ai-audio-description";
    if (!isAiTabOpen) {
      setAiSpeakerOneVoices([]);
      setAiSpeakerOneVoiceGroups([]);
      return undefined;
    }

    let isCurrent = true;
    const timeoutId = window.setTimeout(() => {
      setIsLoadingAiSpeakerOneVoices(true);
      searchAiVoicesRequest({
        ...aiSpeakerOneSettings,
        search: aiSpeakerOneSearch,
        pageToken: aiSpeakerOnePageToken,
      })
        .then((response) => {
          if (!isCurrent) return;
          const voices = Array.isArray(response?.body?.voices)
            ? response.body.voices
            : [];
          setAiSpeakerOneVoices(voices);
          setAiSpeakerOneVoiceGroups(
            Array.isArray(response?.body?.groups) ? response.body.groups : [],
          );
          setAiSpeakerOneNextPageToken(response?.body?.nextPageToken || "");
          const selectedVoice = findAiVoice(voices, aiVoice);
          if (selectedVoice) setAiSpeakerOneSelectedVoice(selectedVoice);
        })
        .catch((error) => {
          if (isCurrent) {
            setAiSpeakerOneVoices([]);
            setAiSpeakerOneVoiceGroups([]);
            setAiError(error?.message || "Could not search Speaker 1 voices.");
          }
        })
        .finally(() => {
          if (isCurrent) setIsLoadingAiSpeakerOneVoices(false);
        });
    }, 350);

    return () => {
      isCurrent = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    aiSpeakerOneSettings,
    aiSpeakerOnePageToken,
    aiSpeakerOneSearch,
    aiVoice,
    descriptionModalTab,
    editor.field,
    editor.isOpen,
  ]);

  useEffect(() => {
    const isAiTabOpen =
      editor.isOpen &&
      editor.field === "description" &&
      descriptionModalTab === "ai-audio-description";
    if (!isAiTabOpen || !hasSecondSpeaker) {
      setAiSpeakerTwoVoices([]);
      setAiSpeakerTwoVoiceGroups([]);
      return undefined;
    }

    let isCurrent = true;
    const timeoutId = window.setTimeout(() => {
      setIsLoadingAiSpeakerTwoVoices(true);
      searchAiVoicesRequest({
        ...aiSpeakerTwoSettings,
        search: aiSpeakerTwoSearch,
        pageToken: aiSpeakerTwoPageToken,
      })
        .then((response) => {
          if (!isCurrent) return;
          const voices = Array.isArray(response?.body?.voices)
            ? response.body.voices
            : [];
          setAiSpeakerTwoVoices(voices);
          setAiSpeakerTwoVoiceGroups(
            Array.isArray(response?.body?.groups) ? response.body.groups : [],
          );
          setAiSpeakerTwoNextPageToken(response?.body?.nextPageToken || "");
          const selectedVoice = findAiVoice(voices, aiSecondVoice);
          if (selectedVoice) setAiSpeakerTwoSelectedVoice(selectedVoice);
        })
        .catch((error) => {
          if (isCurrent) {
            setAiSpeakerTwoVoices([]);
            setAiSpeakerTwoVoiceGroups([]);
            setAiError(error?.message || "Could not search Speaker 2 voices.");
          }
        })
        .finally(() => {
          if (isCurrent) setIsLoadingAiSpeakerTwoVoices(false);
        });
    }, 350);

    return () => {
      isCurrent = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    aiSpeakerTwoSettings,
    aiSpeakerTwoPageToken,
    aiSpeakerTwoSearch,
    aiSecondVoice,
    descriptionModalTab,
    editor.field,
    editor.isOpen,
    hasSecondSpeaker,
  ]);

  function openLocalizedEditor(field, languageIso) {
    const localizedValue = readLocalizedValue(draft?.[field]?.[languageIso]);
    setEditor({ isOpen: true, languageIso, field });
    setEditorHtml(localizedValue);
    if (field === "description") {
      const savedAiDescription = draft?.aiAudioDescription?.[languageIso] || {};
      const savedSpeakerOne = savedAiDescription?.speakers?.[0] || {};
      const savedSpeakerTwo = savedAiDescription?.speakers?.[1] || {};
      setDescriptionModalTab("description");
      setIsDescriptionSaved(hasEditorContent(localizedValue));
      setAiSpeakerOneSettings({
        language: String(
          savedSpeakerOne?.language_code ||
            savedSpeakerOne?.language ||
            savedAiDescription?.language_code ||
            savedAiDescription?.language ||
            "",
        ),
        gender: String(
          savedSpeakerOne?.gender || savedAiDescription?.gender || "",
        ),
        pitch: String(
          savedSpeakerOne?.pitch || savedAiDescription?.pitch || "",
        ),
        style: String(
          savedSpeakerOne?.style || savedAiDescription?.style || "",
        ),
        useCase: String(
          savedSpeakerOne?.use_case ||
            savedSpeakerOne?.useCase ||
            savedAiDescription?.use_case ||
            savedAiDescription?.useCase ||
            "",
        ),
      });
      setAiSpeakerTwoSettings({
        language: String(
          savedSpeakerTwo?.language_code ||
            savedSpeakerTwo?.language ||
            savedAiDescription?.language_code ||
            savedAiDescription?.language ||
            "",
        ),
        gender: String(
          savedSpeakerTwo?.gender || savedAiDescription?.gender || "",
        ),
        pitch: String(
          savedSpeakerTwo?.pitch || savedAiDescription?.pitch || "",
        ),
        style: String(
          savedSpeakerTwo?.style || savedAiDescription?.style || "",
        ),
        useCase: String(
          savedSpeakerTwo?.use_case ||
            savedSpeakerTwo?.useCase ||
            savedAiDescription?.use_case ||
            savedAiDescription?.useCase ||
            "",
        ),
      });
      setHasSecondSpeaker(Boolean(savedAiDescription?.speakers?.length > 1));
      setAiGeneratedDescription(
        htmlToPlainText(
          savedAiDescription?.description ||
            savedAiDescription?.generatedDescription ||
            localizedValue,
        ),
      );
      const savedSpeakerOneVoice = String(
        savedAiDescription?.speakers?.[0]?.voice || "",
      );
      const savedSpeakerTwoVoice = String(
        savedAiDescription?.speakers?.[1]?.voice || "",
      );
      setAiVoice(savedSpeakerOneVoice);
      setAiSecondVoice(savedSpeakerTwoVoice);
      setAiSpeakerOneSelectedVoice(
        savedSpeakerOneVoice
          ? {
              id: savedSpeakerOneVoice,
              name: String(savedSpeakerOne?.speaker || savedSpeakerOneVoice),
              gender: String(savedSpeakerOne?.gender || ""),
            }
          : null,
      );
      setAiSpeakerTwoSelectedVoice(
        savedSpeakerTwoVoice
          ? {
              id: savedSpeakerTwoVoice,
              name: String(savedSpeakerTwo?.speaker || savedSpeakerTwoVoice),
              gender: String(savedSpeakerTwo?.gender || ""),
            }
          : null,
      );
      setAiVoiceAssignments(
        Array.isArray(savedAiDescription?.voiceSegments)
          ? savedAiDescription.voiceSegments.map((segment) => ({
              ...segment,
              voice: String(segment?.voice || ""),
            }))
          : [],
      );
      setAiAudio(savedAiDescription?.audio || null);
    }
  }

  function closeLocalizedEditor() {
    setEditor({ isOpen: false, languageIso: "", field: "description" });
    setEditorHtml("");
  }

  function openAiAudioDescription() {
    if (!aiGeneratedDescription.trim()) {
      setAiGeneratedDescription(htmlToPlainText(editorHtml));
    }
    // Reload the catalog when entering this tab so changed backend filters
    // (notably filters.style) replace any catalog kept from a prior opening.
    setHasLoadedAiCatalog(false);
    setAiError("");
    setDescriptionModalTab("ai-audio-description");
  }

  async function generateAiAudio() {
    if (!canGenerateAiAudio) {
      setAiError(
        "Select a voice for each active speaker and complete the Description before creating audio.",
      );
      return;
    }
    if (!aiGeneratedDescription.trim()) {
      setAiError("Enter a description before creating audio.");
      return;
    }
    if (!aiVoice) {
      setAiError("Select Speaker 1 before creating audio.");
      return;
    }
    if (aiSecondVoice && aiSecondVoice === aiVoice) {
      setAiError("Speaker 1 and Speaker 2 must use different voices.");
      return;
    }
    if (
      hasSecondSpeaker &&
      !aiVoiceAssignments.some(
        (assignment) =>
          assignment?.voice === aiSecondVoice &&
          String(assignment?.text || "").trim(),
      )
    ) {
      setAiError("Assign at least one part of the description to Speaker 2.");
      return;
    }
    setAiError("");
    setIsGeneratingAudio(true);
    try {
      const response = await generateTextToSpeechRequest({
        id: String(draft?.id || point?.id || ""),
        ...buildTtsPayload(
          aiGeneratedDescription,
          {
            ...(aiSpeakerOneSelectedVoice ||
              findAiVoice(aiSpeakerOneVoices, aiVoice) ||
              {}),
            // Search results are rendered from groups, so the flat voices
            // array can be empty even after a voice was selected. The TTS
            // contract always needs the selected voice ID.
            id: aiVoice,
            name:
              aiSpeakerOneSelectedVoice?.name ||
              findAiVoice(aiSpeakerOneVoices, aiVoice)?.name ||
              "Speaker1",
          },
          hasSecondSpeaker
            ? {
                ...(aiSpeakerTwoSelectedVoice ||
                  findAiVoice(aiSpeakerTwoVoices, aiSecondVoice) ||
                  {}),
                id: aiSecondVoice,
                name:
                  aiSpeakerTwoSelectedVoice?.name ||
                  findAiVoice(aiSpeakerTwoVoices, aiSecondVoice)?.name ||
                  "Speaker2",
              }
            : {},
          aiVoiceAssignments,
        ),
      });
      const multimedia = response?.body?.multimedia;
      if (!multimedia?.id || !multimedia?.url) {
        throw new Error("The AI service did not return an audio file.");
      }
      const generatedAudio = {
        ...multimedia,
        url: resolveMediaUrl(String(multimedia.url || "")),
        mediaType: "audio",
        language: currentLanguageIso,
      };
      // AI audio is private to ai_audio_description. It must not be added to
      // the point's regular multimedia/audio section.
      setAiAudio(generatedAudio);
    } catch (error) {
      setAiError(error?.message || "Could not generate audio.");
    } finally {
      setIsGeneratingAudio(false);
    }
  }

  function assignVoiceToGeneratedText(selection) {
    setAiVoiceAssignments((currentAssignments) => {
      const remaining = (
        Array.isArray(currentAssignments) ? currentAssignments : []
      ).flatMap((assignment, index) => {
        if (
          selection.end <= assignment.start ||
          selection.start >= assignment.end
        ) {
          return [assignment];
        }
        const fragments = [];
        if (assignment.start < selection.start) {
          fragments.push({
            ...assignment,
            id: `${assignment.id}-before-${index}`,
            end: selection.start,
            text: aiGeneratedDescription.slice(
              assignment.start,
              selection.start,
            ),
          });
        }
        if (assignment.end > selection.end) {
          fragments.push({
            ...assignment,
            id: `${assignment.id}-after-${index}`,
            start: selection.end,
            text: aiGeneratedDescription.slice(selection.end, assignment.end),
          });
        }
        return fragments;
      });
      // Speaker 1 is the default voice. Selecting it simply returns this
      // range to the unmarked base narration.
      if (selection.voice === aiVoice) return remaining;
      return [
        ...remaining,
        {
          id: `voice-section-${Date.now()}`,
          start: selection.start,
          end: selection.end,
          text: selection.text,
          voice: selection.voice,
        },
      ];
    });
    setAiError("");
  }

  function updateGeneratedDescription(nextText) {
    const previousText = String(aiGeneratedDescription || "");
    const nextValue = String(nextText || "");
    if (nextValue === previousText) return;

    // Keep speaker ranges attached to their text when the user edits the
    // script. In particular, a line break only shifts/extends a range instead
    // of removing every selected speaker.
    let changeStart = 0;
    while (
      changeStart < previousText.length &&
      changeStart < nextValue.length &&
      previousText[changeStart] === nextValue[changeStart]
    ) {
      changeStart += 1;
    }
    let previousChangeEnd = previousText.length;
    let nextChangeEnd = nextValue.length;
    while (
      previousChangeEnd > changeStart &&
      nextChangeEnd > changeStart &&
      previousText[previousChangeEnd - 1] === nextValue[nextChangeEnd - 1]
    ) {
      previousChangeEnd -= 1;
      nextChangeEnd -= 1;
    }
    const removedLength = previousChangeEnd - changeStart;
    const offsetDelta = nextChangeEnd - previousChangeEnd;
    setAiVoiceAssignments((currentAssignments) =>
      (Array.isArray(currentAssignments) ? currentAssignments : [])
        .flatMap((assignment) => {
          const start = Number(assignment?.start) || 0;
          const end = Number(assignment?.end) || 0;
          if (end <= changeStart) return [{ ...assignment, start, end }];
          if (start >= previousChangeEnd) {
            return [
              {
                ...assignment,
                start: start + offsetDelta,
                end: end + offsetDelta,
              },
            ];
          }
          // A pure insertion inside an assigned range (such as Enter) keeps
          // that range with the same speaker.
          if (
            removedLength === 0 &&
            start <= changeStart &&
            end >= changeStart
          ) {
            return [{ ...assignment, start, end: end + offsetDelta }];
          }
          // Replacing/deleting assigned content invalidates only that range.
          return [];
        })
        .map((assignment) => ({
          ...assignment,
          text: nextValue.slice(assignment.start, assignment.end),
        }))
        .filter((assignment) => assignment.start < assignment.end),
    );
    setAiGeneratedDescription(nextValue);
  }

  function changeSpeakerVoice(previousVoice, nextVoice) {
    if (previousVoice && nextVoice && previousVoice !== nextVoice) {
      setAiVoiceAssignments((currentAssignments) =>
        (Array.isArray(currentAssignments) ? currentAssignments : []).map(
          (assignment) =>
            assignment?.voice === previousVoice
              ? { ...assignment, voice: nextVoice }
              : assignment,
        ),
      );
    }
  }

  function addSecondSpeaker() {
    setHasSecondSpeaker(true);
    setAiSpeakerTwoSettings({ ...EMPTY_AI_SPEAKER_SETTINGS });
    setAiSpeakerTwoVoices([]);
    setAiSpeakerTwoVoiceGroups([]);
    setAiSpeakerTwoSearch("");
    setAiSpeakerTwoPageToken("");
    setAiSpeakerTwoPreviousTokens([]);
    setAiSpeakerTwoNextPageToken("");
    setAiSecondVoice("");
    setAiSpeakerTwoSelectedVoice(null);
    setAiError("");
  }

  function removeSecondSpeaker() {
    const removedVoice = aiSecondVoice;
    setHasSecondSpeaker(false);
    setAiSpeakerTwoSettings({ ...EMPTY_AI_SPEAKER_SETTINGS });
    setAiSpeakerTwoVoices([]);
    setAiSpeakerTwoVoiceGroups([]);
    setAiSpeakerTwoSearch("");
    setAiSpeakerTwoPageToken("");
    setAiSpeakerTwoPreviousTokens([]);
    setAiSpeakerTwoNextPageToken("");
    setAiSecondVoice("");
    setAiSpeakerTwoSelectedVoice(null);
    if (removedVoice) {
      setAiVoiceAssignments((currentAssignments) =>
        (Array.isArray(currentAssignments) ? currentAssignments : []).filter(
          (assignment) => assignment?.voice !== removedVoice,
        ),
      );
    }
    setAiError("");
  }

  function updateAiSpeakerSetting(speakerNumber, field, value) {
    const setSettings =
      speakerNumber === 2 ? setAiSpeakerTwoSettings : setAiSpeakerOneSettings;
    setSettings((current) => ({ ...current, [field]: value }));
    if (speakerNumber === 2) {
      const previousVoice = aiSecondVoice;
      setAiSpeakerTwoVoices([]);
      setAiSpeakerTwoVoiceGroups([]);
      setAiSpeakerTwoPageToken("");
      setAiSpeakerTwoPreviousTokens([]);
      setAiSpeakerTwoNextPageToken("");
      setAiSecondVoice("");
      setAiSpeakerTwoSelectedVoice(null);
      if (previousVoice) {
        setAiVoiceAssignments((currentAssignments) =>
          (Array.isArray(currentAssignments) ? currentAssignments : []).filter(
            (assignment) => assignment?.voice !== previousVoice,
          ),
        );
      }
    } else {
      setAiSpeakerOneVoices([]);
      setAiSpeakerOneVoiceGroups([]);
      setAiSpeakerOnePageToken("");
      setAiSpeakerOnePreviousTokens([]);
      setAiSpeakerOneNextPageToken("");
      setAiVoice("");
      setAiSpeakerOneSelectedVoice(null);
    }
    setAiError("");
  }

  function changeAiSpeakerVoicePage(speakerNumber, direction) {
    const isSecondSpeaker = speakerNumber === 2;
    const pageToken = isSecondSpeaker
      ? aiSpeakerTwoPageToken
      : aiSpeakerOnePageToken;
    const previousTokens = isSecondSpeaker
      ? aiSpeakerTwoPreviousTokens
      : aiSpeakerOnePreviousTokens;
    const nextPageToken = isSecondSpeaker
      ? aiSpeakerTwoNextPageToken
      : aiSpeakerOneNextPageToken;
    const setPageToken = isSecondSpeaker
      ? setAiSpeakerTwoPageToken
      : setAiSpeakerOnePageToken;
    const setPreviousTokens = isSecondSpeaker
      ? setAiSpeakerTwoPreviousTokens
      : setAiSpeakerOnePreviousTokens;

    if (direction === "next" && nextPageToken) {
      setPreviousTokens([...previousTokens, pageToken]);
      setPageToken(nextPageToken);
      return;
    }
    if (direction === "previous" && previousTokens.length) {
      setPreviousTokens(previousTokens.slice(0, -1));
      setPageToken(previousTokens[previousTokens.length - 1]);
    }
  }

  async function downloadAiAudio() {
    if (!aiAudio?.url || isDownloadingAiAudio) return;
    const localizedName =
      readLocalizedValue(draft?.name?.[currentLanguageIso]) ||
      readLocalizedValue(draft?.name?.en) ||
      "route point";
    setAiError("");
    setIsDownloadingAiAudio(true);
    try {
      const response = await downloadAudioWithCustomNameRequest({
        url: aiAudio.url,
        language: currentLanguageIso,
        name: localizedName,
      });
      const blob = response?.data;
      if (!(blob instanceof Blob)) {
        throw new Error("The audio file could not be downloaded.");
      }
      const disposition =
        response?.headers?.get?.("content-disposition") ||
        response?.headers?.["content-disposition"] ||
        "";
      const fileNameMatch = disposition.match(
        /filename\*?=(?:UTF-8''|\")?([^\";]+)/i,
      );
      // The API owns the final filename. The point-based name is only used
      // when the response cannot provide Content-Disposition.
      const fileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/\"/g, ""))
        : buildAudioFileName(localizedName, currentLanguageIso);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      setAiError(error?.message || "Could not download audio.");
    } finally {
      setIsDownloadingAiAudio(false);
    }
  }

  function saveLocalizedEditor() {
    if (!draft?.id || !editor.languageIso) return;
    if (
      editor.field === "description" &&
      descriptionModalTab === "ai-audio-description"
    ) {
      if (!canSaveAiAudioDescription) {
        setAiError(
          "Generate the audio and complete all AI Audio Description fields before saving.",
        );
        return;
      }
      const savedAiAudioDescription = {
        ...(draft?.aiAudioDescription?.[editor.languageIso] || {}),
      };
      delete savedAiAudioDescription.scene;
      delete savedAiAudioDescription.sampleContext;
      delete savedAiAudioDescription.generatedDescription;
      delete savedAiAudioDescription.style;
      delete savedAiAudioDescription.language_code;
      delete savedAiAudioDescription.language;
      delete savedAiAudioDescription.gender;
      delete savedAiAudioDescription.accent;
      delete savedAiAudioDescription.pitch;
      delete savedAiAudioDescription.use_case;
      delete savedAiAudioDescription.useCase;
      delete savedAiAudioDescription.persona;
      delete savedAiAudioDescription.context;
      const nextDraft = {
        ...(draft || {}),
        aiAudioDescription: {
          ...(draft?.aiAudioDescription || {}),
          [editor.languageIso]: {
            ...savedAiAudioDescription,
            description: aiGeneratedDescription,
            speakers: [
              {
                speaker:
                  aiSpeakerOneSelectedVoice?.name ||
                  findAiVoice(aiSpeakerOneVoices, aiVoice)?.name ||
                  "Speaker1",
                voice: aiVoice,
                language: aiSpeakerOneSettings.language,
                gender: aiSpeakerOneSettings.gender,
                pitch: aiSpeakerOneSettings.pitch,
                style: aiSpeakerOneSettings.style,
                use_case: aiSpeakerOneSettings.useCase,
              },
              ...(hasSecondSpeaker && aiSecondVoice
                ? [
                    {
                      speaker:
                        aiSpeakerTwoSelectedVoice?.name ||
                        findAiVoice(aiSpeakerTwoVoices, aiSecondVoice)?.name ||
                        "Speaker2",
                      voice: aiSecondVoice,
                      language: aiSpeakerTwoSettings.language,
                      gender: aiSpeakerTwoSettings.gender,
                      pitch: aiSpeakerTwoSettings.pitch,
                      style: aiSpeakerTwoSettings.style,
                      use_case: aiSpeakerTwoSettings.useCase,
                    },
                  ]
                : []),
            ],
            voiceSegments: aiVoiceAssignments,
            audio: aiAudio,
          },
        },
      };
      commitDraftImmediately(nextDraft);
      setAiError("");
      closeLocalizedEditor();
      return;
    }
    const nextDraft = {
      ...(draft || {}),
      [editor.field]: {
        ...(draft?.[editor.field] || {}),
        [editor.languageIso]: editorHtml,
      },
    };
    commitDraftImmediately(nextDraft);
    if (editor.field === "description") {
      setIsDescriptionSaved(hasEditorContent(editorHtml));
      setDescriptionModalTab("description");
      return;
    }
    closeLocalizedEditor();
  }

  function applyAiAudioToPoint() {
    if (!aiAudio?.id || !aiAudio?.url) {
      setAiError("Generate the audio before applying it to this point.");
      return;
    }

    const pointAudio = {
      ...aiAudio,
      mediaType: "audio",
      language: currentLanguageIso,
    };
    const nextDraft = {
      ...(draft || {}),
      multimedia: [
        ...(Array.isArray(draft?.multimedia) ? draft.multimedia : []).filter(
          (item) =>
            !(
              resolveMediaKind(item) === "audio" &&
              String(
                item?.language || item?.languageIso || "",
              ).toLowerCase() === currentLanguageIso
            ),
        ),
        pointAudio,
      ],
    };
    commitDraftImmediately(nextDraft);
    setAiError("");
  }

  if (!draft) return null;

  function commitDraftImmediately(nextDraft) {
    setDraft(nextDraft);
    if (!nextDraft?.id) return;
    onCommitPoint?.(nextDraft.id, {
      ...nextDraft,
      isPersisted: true,
    });
  }

  const currentLanguageIso = languageList.some(
    (language) => language.iso === activeLanguageIso,
  )
    ? activeLanguageIso
    : languageList[0]?.iso || "";
  const isEnglishLanguage = String(currentLanguageIso).toLowerCase() === "en";
  const currentLanguageLabel =
    languageList.find((language) => language.iso === currentLanguageIso)
      ?.name || currentLanguageIso.toUpperCase();
  const currentAiAudioDescription =
    draft?.aiAudioDescription?.[currentLanguageIso] ||
    draft?.ai_audio_description?.[currentLanguageIso] ||
    {};
  const hasAiAudioDescription = Boolean(
    currentAiAudioDescription?.audio?.id ||
    currentAiAudioDescription?.audio?.url,
  );
  const drivingRouteGroupOptions =
    selectedMode === "driving" ? DRIVING_ALTERNATE_ROUTE_KEYS : [];
  const routeGroupSelectValue = getRouteGroupSelectValue(draft);
  const aiSpeakerChoices = [
    {
      id: aiVoice,
      name:
        aiSpeakerOneSelectedVoice?.name ||
        findAiVoice(aiSpeakerOneVoices, aiVoice)?.name ||
        aiVoice,
      gender:
        aiSpeakerOneSelectedVoice?.gender ||
        findAiVoice(aiSpeakerOneVoices, aiVoice)?.gender ||
        "",
    },
    {
      id: aiSecondVoice,
      name:
        aiSpeakerTwoSelectedVoice?.name ||
        findAiVoice(aiSpeakerTwoVoices, aiSecondVoice)?.name ||
        aiSecondVoice,
      gender:
        aiSpeakerTwoSelectedVoice?.gender ||
        findAiVoice(aiSpeakerTwoVoices, aiSecondVoice)?.gender ||
        "",
    },
  ].filter(
    (voice, index, allVoices) =>
      voice.id &&
      (!index || hasSecondSpeaker) &&
      allVoices.findIndex((item) => item.id === voice.id) === index,
  );
  const canGenerateAiAudio = Boolean(
    String(aiGeneratedDescription || "").trim() &&
    aiVoice &&
    (!hasSecondSpeaker || (aiSecondVoice && aiVoice !== aiSecondVoice)),
  );
  const canSaveAiAudioDescription = Boolean(canGenerateAiAudio && aiAudio?.id);

  const radiusValue = Number(draft?.radiusMeters ?? 0);
  const turnCaseValue = Number(draft?.turnCase);
  const activeTurnType =
    TURN_TYPE_DEFINITIONS.find(
      (option) => Number(option.value) === Number(turnCaseValue),
    ) || null;
  const ActiveTurnTypeIcon =
    TURN_TYPE_ICON_BY_KEY[activeTurnType?.key] || IconMapPin;
  const activePointType =
    POINT_TYPE_OPTIONS.find(
      (option) => String(option.value) === String(draft?.pointType || ""),
    ) || null;
  const ActivePointTypeIcon = activePointType?.Icon || IconHome2;
  const radiusError = hasRoutePointRadius(radiusValue)
    ? ""
    : "Radius must be greater than 0.";
  const currentNameValue = readLocalizedValue(
    draft?.name?.[currentLanguageIso],
  );
  const englishNameValue = readLocalizedValue(draft?.name?.en);
  const isNewPoint = isNewRoutePoint(draft);
  const nameError =
    isNewPoint &&
    currentLanguageIso === "en" &&
    !String(englishNameValue || "").trim()
      ? "EN name is required."
      : "";
  const caseError = hasRoutePointTurnCase(turnCaseValue)
    ? ""
    : "Turn Type is required.";
  const pointTypeError = hasRoutePointType(draft?.pointType, selectedMode)
    ? ""
    : "Point Type is required.";

  const multimedia = Array.isArray(draft?.multimedia) ? draft.multimedia : [];
  const imageItems = multimedia.filter(
    (item) => resolveMediaKind(item) === "image",
  );
  const audioItems = multimedia.filter(
    (item) =>
      resolveMediaKind(item) === "audio" &&
      String(item?.language || item?.languageIso || "").toLowerCase() ===
        currentLanguageIso,
  );
  const audioMediaCount = multimedia.filter(
    (item) => resolveMediaKind(item) === "audio",
  ).length;
  const musicItems = Array.isArray(draft?.music)
    ? draft.music
    : draft?.music?.id
      ? [{ ...draft.music, language: draft.music.language || "en" }]
      : [];
  const musicItem =
    musicItems.find(
      (item) =>
        String(item?.language || item?.languageIso || "").toLowerCase() ===
        currentLanguageIso,
    ) || null;
  const mixedAudioItems = Array.isArray(draft?.mixed_audio)
    ? draft.mixed_audio
    : draft?.mixed_audio?.id
      ? [draft.mixed_audio]
      : [];
  const mixedAudioItem =
    mixedAudioItems.find(
      (item) =>
        String(item?.language || "").toLowerCase() === currentLanguageIso,
    ) || null;
  function reorderImages(nextImages = []) {
    const date = new Date().toISOString().slice(0, 10);
    const orderedImages = nextImages.map((item, index) => ({
      ...item,
      updatedAt: `${date}T${String(index).padStart(2, "0")}:00:00.000Z`,
    }));
    let imageIndex = 0;
    const nextMultimedia = multimedia.map((item) => {
      if (resolveMediaKind(item) !== "image") return item;
      return orderedImages[imageIndex++] || item;
    });
    commitDraftImmediately({ ...(draft || {}), multimedia: nextMultimedia });
  }
  const mediaError = hasRoutePointMedia(draft)
    ? ""
    : "Add at least 1 image or audio.";

  return (
    <div className="mode-route-tool-point-editor">
      <div className="mode-route-tool-point-editor-head">
        <p className="mode-route-tool-subtitle">
          Edit point #{draft.order || "-"}
        </p>
        <div className="mode-route-tool-point-editor-actions">
          {currentLanguageIso === "en" ? (
            <>
              <button
                type="button"
                className="app-icon-btn app-icon-btn--add"
                aria-label="Translate point"
                title="Translate point"
                onClick={(event) => {
                  event.stopPropagation();
                  onTranslatePoint?.(draft);
                }}
              >
                <IconLanguage size={14} />
              </button>
              <button
                type="button"
                className="app-icon-btn app-icon-btn--add"
                aria-label="Generate point image"
                title="Generate point image"
                onClick={(event) => {
                  event.stopPropagation();
                  onGeneratePointImage?.(draft);
                }}
              >
                <IconPhoto size={14} />
              </button>
              <button
                type="button"
                className="app-icon-btn app-icon-btn--add"
                aria-label={`Generate AI music for this point in ${currentLanguageIso.toUpperCase()}`}
                title={
                  isGeneratingMusic
                    ? `Generating AI music Background in ${currentLanguageIso.toUpperCase()}`
                    : `Generate AI music Background for this point in ${currentLanguageIso.toUpperCase()}`
                }
                disabled={isGeneratingMusic}
                onClick={(event) => {
                  event.stopPropagation();
                  onGeneratePointMusic?.(draft, currentLanguageIso);
                }}
              >
                {isGeneratingMusic ? (
                  <span className="mode-route-tool-btn-spinner" />
                ) : (
                  <IconMusic size={14} />
                )}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="app-icon-btn app-icon-btn--delete"
            aria-label="Delete point"
            title="Delete point"
            onClick={() => onDeletePoint?.(draft?.id)}
          >
            <IconTrash size={14} />
          </button>
        </div>
      </div>

      <div>
        <p className="tour-add-form-label">
          name
          {touched.name && nameError ? (
            <InlineError>{nameError}</InlineError>
          ) : null}
        </p>
        <label className="field-label app-form-field mode-route-field">
          <input
            className="field-input"
            value={currentNameValue}
            onChange={(event) => {
              const nextDraft = {
                ...(draft || {}),
                name: {
                  ...(draft?.name || {}),
                  [currentLanguageIso]: event.target.value,
                },
              };
              commitDraftImmediately(nextDraft);
              setTouched((prev) => ({
                ...prev,
                name: isNewPoint
                  ? true
                  : !hasRoutePointName(nextDraft.name, nextDraft),
              }));
            }}
            onBlur={() =>
              setTouched((prev) => ({
                ...prev,
                name: isNewPoint
                  ? true
                  : !hasRoutePointName(draft?.name, draft),
              }))
            }
          />
        </label>
      </div>

      <div>
        <div className="mode-route-description-label-row">
          <p className="tour-add-form-label">description</p>
          {hasAiAudioDescription ? (
            <span
              className="mode-route-ai-audio-ready-badge"
              title={`AI audio description ready in ${currentLanguageLabel}`}
            >
              <IconSparkles size={13} />
              AI audio description ready
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className={`field-label app-form-field field-preview-row ${readLocalizedValue(draft?.description?.[currentLanguageIso]) ? "filled" : ""}`}
          onClick={() => openLocalizedEditor("description", currentLanguageIso)}
        >
          {readLocalizedValue(draft?.description?.[currentLanguageIso]) ? (
            <span
              className="field-preview-value field-preview-value-html"
              dangerouslySetInnerHTML={{
                __html: readLocalizedValue(
                  draft?.description?.[currentLanguageIso],
                ),
              }}
            />
          ) : (
            <span className="field-preview-value">Complete</span>
          )}
        </button>
      </div>

      <div>
        <p className="tour-add-form-label">more info</p>
        <button
          type="button"
          className={`field-label app-form-field field-preview-row ${readLocalizedValue(draft?.more_info?.[currentLanguageIso]) ? "filled" : ""}`}
          onClick={() => openLocalizedEditor("more_info", currentLanguageIso)}
        >
          {readLocalizedValue(draft?.more_info?.[currentLanguageIso]) ? (
            <span
              className="field-preview-value field-preview-value-html"
              dangerouslySetInnerHTML={{
                __html: readLocalizedValue(
                  draft?.more_info?.[currentLanguageIso],
                ),
              }}
            />
          ) : (
            <span className="field-preview-value">Complete</span>
          )}
        </button>
      </div>

      {selectedMode === "driving" && isEnglishLanguage ? (
        <>
          <label className="field-label app-form-field mode-route-field">
            <span className="label-inline">
              Radius meters
              {touched.radiusMeters && radiusError ? (
                <InlineError>{radiusError}</InlineError>
              ) : null}
            </span>
            <input
              className="field-input"
              type="number"
              min="0.01"
              step="1"
              value={Number(draft?.radiusMeters ?? 0) || ""}
              onChange={(event) => {
                const nextValue = Number(event.target.value || 0);
                const nextDraft = {
                  ...(draft || {}),
                  radiusMeters: nextValue,
                };
                commitDraftImmediately(nextDraft);
                setTouched((prev) => ({ ...prev, radiusMeters: true }));
              }}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, radiusMeters: true }))
              }
            />
          </label>
          <label className="field-label app-form-field mode-route-field">
            <span className="label-inline">
              Turn Type
              {touched.turnCase && caseError ? (
                <InlineError>{caseError}</InlineError>
              ) : null}
            </span>
            <div className="mode-route-turn-type-picker">
              <button
                type="button"
                className="mode-route-turn-type-trigger"
                onClick={() => setIsTurnTypePickerOpen((prev) => !prev)}
              >
                <span className="mode-route-turn-type-trigger-main">
                  <ActiveTurnTypeIcon size={15} />
                  <span>
                    {activeTurnType
                      ? `${activeTurnType.label} (${activeTurnType.value})`
                      : "Select turn type"}
                  </span>
                </span>
                <IconChevronDown size={14} />
              </button>
              {isTurnTypePickerOpen ? (
                <div className="mode-route-turn-type-menu">
                  {TURN_TYPE_DEFINITIONS.map((option) => {
                    const IconComponent =
                      TURN_TYPE_ICON_BY_KEY[option.key] || IconMapPin;
                    const isActive =
                      Number(turnCaseValue) === Number(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`mode-route-turn-type-item ${isActive ? "active" : ""}`}
                        onClick={() => {
                          const nextDraft = {
                            ...(draft || {}),
                            turnCase: Number(option.value),
                          };
                          commitDraftImmediately(nextDraft);
                          setTouched((prev) => ({ ...prev, turnCase: true }));
                          setIsTurnTypePickerOpen(false);
                        }}
                      >
                        <IconComponent size={14} />
                        <span>{option.label}</span>
                        <small>{option.value}</small>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </label>
        </>
      ) : null}
      {selectedMode === "driving" && isEnglishLanguage ? (
        <label className="field-label app-form-field mode-route-field">
          <span className="label-inline">Route Group</span>
          <div className="mode-route-turn-type-picker">
            <button
              type="button"
              className="mode-route-turn-type-trigger"
              onClick={() => setIsRouteGroupPickerOpen((prev) => !prev)}
            >
              <span className="mode-route-turn-type-trigger-main">
                <IconMapPin size={15} />
                <span>
                  {routeGroupSelectValue === "main"
                    ? "Main"
                    : `Route ${routeGroupSelectValue}`}
                </span>
              </span>
              <IconChevronDown size={14} />
            </button>
            {isRouteGroupPickerOpen ? (
              <div className="mode-route-turn-type-menu">
                <button
                  type="button"
                  className={`mode-route-turn-type-item ${routeGroupSelectValue === "main" ? "active" : ""}`}
                  onClick={() => {
                    const ownRouteGroup = "main";
                    commitDraftImmediately({
                      ...(draft || {}),
                      routeGroup: ownRouteGroup,
                      routeGroupName: buildDrivingRouteGroupName(
                        draft?.order,
                        ownRouteGroup,
                      ),
                      routeGroupParentPointId: "",
                    });
                    setIsRouteGroupPickerOpen(false);
                  }}
                >
                  <IconMapPin size={14} />
                  <span>Main</span>
                  <small>Continuation</small>
                </button>
                {drivingRouteGroupOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={`mode-route-turn-type-item ${routeGroupSelectValue === String(item || "") ? "active" : ""}`}
                    onClick={() => {
                      commitDraftImmediately({
                        ...(draft || {}),
                        routeGroup: String(item || ""),
                        routeGroupName: buildDrivingRouteGroupName(
                          draft?.order,
                          item,
                        ),
                        routeGroupParentPointId: "",
                      });
                      setIsRouteGroupPickerOpen(false);
                    }}
                  >
                    <IconMapPin size={14} />
                    <span>Route {item}</span>
                    <small>
                      {buildDrivingRouteGroupName(draft?.order, item)}
                    </small>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {routeGroupSelectValue !== "main" ? (
            <small className="field-help-text">
              Route group name:{" "}
              {buildDrivingRouteGroupName(draft?.order, routeGroupSelectValue)}
            </small>
          ) : null}
        </label>
      ) : null}
      {selectedMode === "random" && isEnglishLanguage ? (
        <label className="field-label app-form-field mode-route-field">
          <span className="label-inline">
            Point Type
            {touched.pointType && pointTypeError ? (
              <InlineError>{pointTypeError}</InlineError>
            ) : null}
          </span>
          <div className="mode-route-turn-type-picker">
            <button
              type="button"
              className="mode-route-turn-type-trigger"
              onClick={() => setIsPointTypePickerOpen((prev) => !prev)}
            >
              <span className="mode-route-turn-type-trigger-main">
                <ActivePointTypeIcon size={15} />
                <span>
                  {activePointType
                    ? activePointType.label
                    : "Select point type"}
                </span>
              </span>
              <IconChevronDown size={14} />
            </button>
            {isPointTypePickerOpen ? (
              <div className="mode-route-turn-type-menu">
                {POINT_TYPE_OPTIONS.map((option) => {
                  const isActive =
                    String(draft?.pointType || "") === option.value;
                  const IconComponent = option.Icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`mode-route-turn-type-item ${isActive ? "active" : ""}`}
                      onClick={() => {
                        const nextDraft = {
                          ...(draft || {}),
                          pointType: option.value,
                        };
                        commitDraftImmediately(nextDraft);
                        setTouched((prev) => ({ ...prev, pointType: true }));
                        setIsPointTypePickerOpen(false);
                      }}
                    >
                      <IconComponent size={14} />
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </label>
      ) : null}

      {isEnglishLanguage ? (
        <MediaPicker
          title="images"
          titleError={mediaError}
          items={imageItems}
          isUploading={false}
          uploadError=""
          accept={{ "image/*": [] }}
          invalidTypeMessage="Images only."
          onUpload={async (files) => {
            const uploaded = await onUploadPointMedia?.(files);
            if (!Array.isArray(uploaded) || !uploaded.length) return;
            const nextDraft = {
              ...(draft || {}),
              multimedia: [...(draft?.multimedia || []), ...uploaded],
            };
            commitDraftImmediately(nextDraft);
          }}
          onRemove={(mediaId) =>
            commitDraftImmediately({
              ...(draft || {}),
              multimedia: (draft?.multimedia || []).filter(
                (item) => item.id !== mediaId,
              ),
            })
          }
          onReorder={reorderImages}
          uploadLabel="Upload image"
          uploadHint={
            mediaError
              ? "Required: upload at least 1 image or audio."
              : "Upload images for this mode point."
          }
        />
      ) : null}

      <MediaPicker
        title="audios"
        items={audioItems}
        isUploading={false}
        uploadError=""
        accept={{ "audio/*": [] }}
        maxFiles={1}
        invalidTypeMessage="Audio only."
        onUpload={async (files) => {
          const uploaded = await onUploadPointMedia?.(files, {
            language: currentLanguageIso,
            mediaRole: "pointAudio",
          });
          if (!Array.isArray(uploaded) || !uploaded.length) return;
          const nextDraft = {
            ...(draft || {}),
            multimedia: [
              ...(draft?.multimedia || []).filter(
                (item) =>
                  !(
                    resolveMediaKind(item) === "audio" &&
                    String(
                      item?.language || item?.languageIso || "",
                    ).toLowerCase() === currentLanguageIso
                  ),
              ),
              ...uploaded,
            ],
          };
          commitDraftImmediately(nextDraft);
        }}
        onRemove={(mediaId) =>
          commitDraftImmediately({
            ...(draft || {}),
            multimedia: (draft?.multimedia || []).filter(
              (item) => item.id !== mediaId,
            ),
          })
        }
        allowAudioRemoval
        uploadLabel={`Upload audio (${currentLanguageLabel})`}
        uploadHint="Upload one audio for the selected language."
      />

      <div className="mode-route-point-music-field">
        <p className="tour-add-form-label">music</p>
        {musicItem?.url ? (
          <AiAudioWavePreview
            url={resolveMediaUrl(String(musicItem.url))}
            showDownload={false}
            onRemove={() =>
              commitDraftImmediately({
                ...(draft || {}),
                music: musicItems.filter(
                  (item) =>
                    String(
                      item?.language || item?.languageIso || "",
                    ).toLowerCase() !== currentLanguageIso,
                ),
              })
            }
          />
        ) : (
          <p className="mode-route-point-music-empty">
            Generated music will appear here.
          </p>
        )}
        <button
          type="button"
          className="mode-route-point-mix-button"
          disabled={isAudioMixBusy}
          onClick={async () => {
            const mixedPoint = await onMixAudioMusic?.(
              draft,
              currentLanguageIso,
            );
            if (mixedPoint) setDraft(mixedPoint);
          }}
          title={
            isMixingAudio
              ? "Mixing voice and music"
              : "Mix voice audio with this point's music"
          }
        >
          {isMixingAudio ? (
            <span className="mode-route-tool-btn-spinner" aria-hidden="true" />
          ) : (
            <>
              <IconArrowsJoin size={18} aria-hidden="true" />
              <IconMusic size={17} aria-hidden="true" />
            </>
          )}
          <span>
            {isMixingAudio ? "Mixing Audio + Music…" : "Mix Audio + Music"}
          </span>
        </button>
        {audioMixError ? (
          <p className="mode-route-point-mix-error" role="alert">
            {audioMixError}
          </p>
        ) : null}
        <div className="mode-route-point-mixed-audio">
          <p className="tour-add-form-label">Audio Mixing</p>
          {mixedAudioItem?.url ? (
            <AiAudioWavePreview
              url={resolveMediaUrl(String(mixedAudioItem.url))}
              showDownload={false}
            />
          ) : (
            <p className="mode-route-point-music-empty">
              {isMixingAudio
                ? "Waiting for the new mix…"
                : "The mixed audio will appear here."}
            </p>
          )}
        </div>
      </div>

      {isEnglishLanguage ? (
        <div>
          <p className="tour-add-form-label">notes</p>
          <label className="field-label app-form-field mode-route-field">
            <textarea
              className="field-input"
              rows={4}
              value={String(draft?.notes || "")}
              onChange={(event) =>
                commitDraftImmediately({
                  ...(draft || {}),
                  notes: event.target.value,
                })
              }
              placeholder="Add notes for this route point"
            />
          </label>
        </div>
      ) : null}

      {isEnglishLanguage ? (
        <div>
          <p className="tour-add-form-label">AI brief</p>
          <label className="field-label app-form-field mode-route-field">
            <textarea
              className="field-input"
              rows={4}
              value={String(draft?.ai_brief || "")}
              onChange={(event) =>
                commitDraftImmediately({
                  ...(draft || {}),
                  ai_brief: event.target.value,
                })
              }
              placeholder="Add an AI brief for this route point"
            />
          </label>
        </div>
      ) : null}

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeLocalizedEditor}
        title={`${String(editor.field || "description").replace("_", " ")} (${editor.languageIso})`}
        size="lg"
        className={`mode-route-description-modal ${
          descriptionModalTab === "ai-audio-description"
            ? "is-ai-audio-description"
            : ""
        }`}
      >
        <div className="tour-add-editor">
          {editor.field === "description" ? (
            <>
              <div className="mode-route-description-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={descriptionModalTab === "description"}
                  className={
                    descriptionModalTab === "description" ? "active" : ""
                  }
                  onClick={() => setDescriptionModalTab("description")}
                >
                  Description
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={descriptionModalTab === "ai-audio-description"}
                  className={
                    descriptionModalTab === "ai-audio-description"
                      ? "active"
                      : ""
                  }
                  disabled={!isDescriptionSaved}
                  onClick={openAiAudioDescription}
                >
                  AI Audio Description
                </button>
              </div>

              <div
                role="tabpanel"
                hidden={descriptionModalTab !== "description"}
                style={{
                  display:
                    descriptionModalTab === "description" ? undefined : "none",
                }}
              >
                <ModalQuillEditor
                  key={`description-${editor.languageIso}`}
                  value={editorHtml}
                  onChange={setEditorHtml}
                />
              </div>

              {isDescriptionSaved ? (
                <div
                  className="mode-route-description-ai-form"
                  role="tabpanel"
                  hidden={descriptionModalTab !== "ai-audio-description"}
                  style={{
                    display:
                      descriptionModalTab === "ai-audio-description"
                        ? undefined
                        : "none",
                  }}
                >
                  <AiSpeakerConfiguration
                    number={1}
                    settings={aiSpeakerOneSettings}
                    catalog={aiNarrationOptions}
                    voices={aiSpeakerOneVoices}
                    voiceGroups={aiSpeakerOneVoiceGroups}
                    selectedVoice={aiVoice}
                    selectedVoiceDetails={aiSpeakerOneSelectedVoice}
                    isLoadingCatalog={isLoadingAiCatalog}
                    isLoadingVoices={isLoadingAiSpeakerOneVoices}
                    disabledVoice={aiSecondVoice}
                    searchTerm={aiSpeakerOneSearch}
                    onSearchChange={(value) => {
                      setAiSpeakerOneSearch(value);
                      setAiSpeakerOnePageToken("");
                      setAiSpeakerOnePreviousTokens([]);
                      setAiSpeakerOneNextPageToken("");
                    }}
                    hasPreviousPage={aiSpeakerOnePreviousTokens.length > 0}
                    hasNextPage={Boolean(aiSpeakerOneNextPageToken)}
                    onPreviousPage={() =>
                      changeAiSpeakerVoicePage(1, "previous")
                    }
                    onNextPage={() => changeAiSpeakerVoicePage(1, "next")}
                    onSettingChange={(field, value) =>
                      updateAiSpeakerSetting(1, field, value)
                    }
                    onVoiceChange={(nextVoice) => {
                      changeSpeakerVoice(aiVoice, nextVoice);
                      setAiVoice(nextVoice);
                      setAiSpeakerOneSelectedVoice(
                        findAiVoice(aiSpeakerOneVoices, nextVoice),
                      );
                    }}
                  />

                  {hasSecondSpeaker ? (
                    <AiSpeakerConfiguration
                      number={2}
                      settings={aiSpeakerTwoSettings}
                      catalog={aiNarrationOptions}
                      voices={aiSpeakerTwoVoices}
                      voiceGroups={aiSpeakerTwoVoiceGroups}
                      selectedVoice={aiSecondVoice}
                      selectedVoiceDetails={aiSpeakerTwoSelectedVoice}
                      isLoadingCatalog={isLoadingAiCatalog}
                      isLoadingVoices={isLoadingAiSpeakerTwoVoices}
                      disabledVoice={aiVoice}
                      searchTerm={aiSpeakerTwoSearch}
                      onSearchChange={(value) => {
                        setAiSpeakerTwoSearch(value);
                        setAiSpeakerTwoPageToken("");
                        setAiSpeakerTwoPreviousTokens([]);
                        setAiSpeakerTwoNextPageToken("");
                      }}
                      hasPreviousPage={aiSpeakerTwoPreviousTokens.length > 0}
                      hasNextPage={Boolean(aiSpeakerTwoNextPageToken)}
                      onPreviousPage={() =>
                        changeAiSpeakerVoicePage(2, "previous")
                      }
                      onNextPage={() => changeAiSpeakerVoicePage(2, "next")}
                      onSettingChange={(field, value) =>
                        updateAiSpeakerSetting(2, field, value)
                      }
                      onVoiceChange={(nextVoice) => {
                        changeSpeakerVoice(aiSecondVoice, nextVoice);
                        setAiSecondVoice(nextVoice);
                        setAiSpeakerTwoSelectedVoice(
                          findAiVoice(aiSpeakerTwoVoices, nextVoice),
                        );
                      }}
                      onRemove={removeSecondSpeaker}
                    />
                  ) : (
                    <button
                      type="button"
                      className="mode-route-description-ai-add-speaker"
                      onClick={addSecondSpeaker}
                    >
                      + Add Speaker 2
                    </button>
                  )}

                  <div className="mode-route-description-ai-section mode-route-description-ai-description">
                    <span className="mode-route-description-ai-section-label">
                      Description
                    </span>
                    <VoiceAnnotatedTextEditor
                      value={aiGeneratedDescription}
                      voices={aiSpeakerChoices}
                      expressions={aiNarrationOptions.vocalTags}
                      baseVoice={aiVoice}
                      assignments={aiVoiceAssignments}
                      onChange={updateGeneratedDescription}
                      onAssign={assignVoiceToGeneratedText}
                    />
                  </div>

                  <div className="mode-route-description-ai-section mode-route-description-ai-audio">
                    <span className="mode-route-description-ai-section-label">
                      Generated Audio
                    </span>
                    <button
                      type="button"
                      className="button-primary mode-route-description-ai-generate"
                      onClick={generateAiAudio}
                      disabled={isGeneratingAudio || !canGenerateAiAudio}
                    >
                      <IconPlayerPlay size={17} />
                      {isGeneratingAudio
                        ? "Generating audio..."
                        : "Generate audio"}
                    </button>
                    {aiAudio?.url ? (
                      <AiAudioWavePreview
                        url={aiAudio.url}
                        onDownload={downloadAiAudio}
                        isDownloading={isDownloadingAiAudio}
                        isGenerating={isGeneratingAudio}
                      />
                    ) : (
                      <div className="mode-route-description-ai-audio-placeholder">
                        Audio will appear here
                      </div>
                    )}
                  </div>
                  <InlineError>{aiError}</InlineError>
                </div>
              ) : null}
            </>
          ) : null}
          {editor.field !== "description" ? (
            <ModalQuillEditor
              key={`${editor.field}-${editor.languageIso}`}
              value={editorHtml}
              onChange={setEditorHtml}
            />
          ) : null}
          <div
            className={`tour-add-editor-actions ${
              editor.field === "description" &&
              descriptionModalTab === "ai-audio-description"
                ? "is-ai-audio-description-actions"
                : ""
            }`}
          >
            <button
              type="button"
              className="button-ghost"
              onClick={closeLocalizedEditor}
            >
              Cancel
            </button>
            <div className="tour-add-editor-actions-right">
              <button
                type="button"
                className="button-primary"
                onClick={saveLocalizedEditor}
                disabled={
                  editor.field === "description" &&
                  descriptionModalTab === "ai-audio-description" &&
                  !canSaveAiAudioDescription
                }
              >
                Save
              </button>
              {editor.field === "description" &&
              descriptionModalTab === "ai-audio-description" ? (
                <button
                  type="button"
                  className="button-ghost"
                  onClick={applyAiAudioToPoint}
                  disabled={!aiAudio?.id || !aiAudio?.url}
                >
                  Apply
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </AppModal>
    </div>
  );
}

export default ModeToolPointEditor;
